import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Brain, Database, Users, GraduationCap, MessageCircle } from 'lucide-react';

interface QuestionCategory {
  key: string;
  label: string;
  description: string;
  types: string[];
  count: number;
  generation_type: string;
}

interface QuestionTypeSelectorProps {
  categories: QuestionCategory[];
  selectedCategory: string | null;
  onSelectCategory: (category: string) => void;
  loading?: boolean;
}

const categoryIcons = {
  generic_ai: Brain,
  product_facts: Database,
  product_experts: Users,
  external_experts: GraduationCap,
  user_experience: MessageCircle,
};

const categoryColors = {
  generic_ai: 'bg-blue-100 text-blue-800 border-blue-200',
  product_facts: 'bg-green-100 text-green-800 border-green-200',
  product_experts: 'bg-purple-100 text-purple-800 border-purple-200',
  external_experts: 'bg-orange-100 text-orange-800 border-orange-200',
  user_experience: 'bg-pink-100 text-pink-800 border-pink-200',
};

export function QuestionTypeSelector({
  categories,
  selectedCategory,
  onSelectCategory,
  loading = false
}: QuestionTypeSelectorProps) {
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader>
              <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </CardHeader>
            <CardContent>
              <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>No Questions Available</CardTitle>
          <CardDescription>
            This Question Wheel doesn't have any categorized questions yet.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">Select Question Type</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Choose a category to generate answers for those questions
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {categories.map((category) => {
          const Icon = categoryIcons[category.key as keyof typeof categoryIcons] || Brain;
          const colorClass = categoryColors[category.key as keyof typeof categoryColors] || 'bg-gray-100 text-gray-800 border-gray-200';
          const isSelected = selectedCategory === category.key;

          return (
            <Card
              key={category.key}
              className={`cursor-pointer transition-all hover:shadow-md ${
                isSelected ? 'ring-2 ring-primary shadow-md' : ''
              }`}
              onClick={() => onSelectCategory(category.key)}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`p-2 rounded-lg ${colorClass}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle className="text-base">{category.label}</CardTitle>
                    </div>
                  </div>
                  <Badge variant="secondary" className="ml-2">
                    {category.count}
                  </Badge>
                </div>
                <CardDescription className="mt-2 line-clamp-2">
                  {category.description}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    Generation type: <span className="font-medium">{category.generation_type.replace('_', ' ')}</span>
                  </span>
                  {isSelected && (
                    <Button size="sm" variant="default">
                      Selected
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {selectedCategory && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-900">
            <strong>Next step:</strong> Preview and select questions to generate answers for, or start generation for all questions in this category.
          </p>
        </div>
      )}
    </div>
  );
}
